import { createPublicClient, http, type Hex, type Address, BaseError, ContractFunctionRevertedError } from 'viem';
import { base, baseSepolia } from 'viem/chains';
import { TAKEOVER_MANAGER_ABI, UGM_ABI } from './abi';
import { getContractAddresses, getManagerForPool, isV2Manager } from './addresses';

const chainId = process.env.NEXT_PUBLIC_CHAIN_ID;
const chain = chainId === '8453' ? base : baseSepolia;
const rpcUrl = process.env.NEXT_PUBLIC_RPC_URL || chain.rpcUrls.default.http[0];

/** Sentinel string returned by {@link simulateContractCall} when the revert
 *  maps to a native-balance failure (e.g. `'insufficient funds for gas'` or
 *  `'exceeds balance'`). Callers can compare against this exact value to
 *  branch into a swap-widget flow instead of surfacing a hard error. */
export const SIM_ERROR_INSUFFICIENT_FUNDS = 'Insufficient funds for transaction';

const ERROR_MESSAGES: Record<string, string> = {
  GridDoesNotExist: 'This grid does not exist',
  GridPaused: 'This grid is currently paused',
  GridIsDeprecated: 'This grid has been deprecated',
  NotSeatHolder: 'You are not the holder of this tile',
  CooldownActive: 'Price change cooldown is still active (20 minutes between changes)',
  SeatNotClaimed: 'This tile has been forfeited (deposit ran out from taxes)',
  SeatAlreadyClaimed: 'This tile is already claimed',
  SeatDoesNotExist: 'Tile ID is out of range',
  PriceMustBePositive: 'Price must be greater than zero',
  InsufficientDeposit: 'Deposit amount is insufficient',
  InsufficientBalance: 'Insufficient balance for this withdrawal',
  InsufficientPayment: 'Payment is less than the current tile price',
  PoolNotRegistered: 'This pool is not registered',
  PriceExceedsMax: 'Current price exceeds your max price limit',
  CannotBuyOwnSeat: 'You cannot buy your own tile',
  TransferFailed: 'ETH transfer failed',
  TokenNotApprovedForDeposit: 'This Flaunch NFT is not approved for deposit',
  NotAllSeatsHeld: 'Creator must hold all 100 seats to withdraw the coin',
  NotApprovedZap: 'Caller is not an approved zap contract',
  AntiSnipeActive: 'This tile is in a priority claim window — please wait for your turn',
  GridCreationIsPaused: 'Grid creation is currently paused',
  OnlyOneTaxTokenAllowed: 'Only one tax token can be active at a time',
};

/**
 * Simulate a contract call before sending to the wallet.
 * Returns null on success, or a human-readable error string on revert.
 * Pass managerAddress to target a legacy manager instead of the main one.
 */
export async function simulateContractCall(
  functionName: string,
  args: readonly unknown[],
  account: Hex,
  value?: bigint,
  managerAddress?: string,
): Promise<string | null> {
  const publicClient = createPublicClient({ chain, transport: http(rpcUrl) });
  const mgr = getManagerForPool(managerAddress || '');

  const abi = isV2Manager(managerAddress || '') ? UGM_ABI : TAKEOVER_MANAGER_ABI;

  try {
    await publicClient.simulateContract({
      address: mgr,
      abi,
      functionName: functionName as any,
      args: args as any,
      account,
      ...(value !== undefined ? { value } : {}),
    } as any);
    return null;
  } catch (err) {
    if (err instanceof BaseError) {
      const revertError = err.walk(e => e instanceof ContractFunctionRevertedError);
      if (revertError instanceof ContractFunctionRevertedError) {
        const errorName = revertError.data?.errorName;
        if (errorName && ERROR_MESSAGES[errorName]) {
          return ERROR_MESSAGES[errorName];
        }
        if (errorName) {
          return `Contract error: ${errorName}`;
        }
      }
    }
    const msg = err instanceof Error ? err.message : String(err);
    if (msg.includes('insufficient funds') || msg.includes('exceeds balance')) {
      return SIM_ERROR_INSUFFICIENT_FUNDS;
    }
    console.error('[simulate] Unexpected error:', err);
    return msg.length > 200 ? msg.slice(0, 200) + '...' : msg;
  }
}
